<!--
 * COPYRIGHT Transtron Inc 2023
 *
 * 機能名：バッテリー交換画面
 *
 * 作成者：2023/9/14 ノア）唐
 * 最終修正者：2023/9/14 ノア）唐
 *
 -->
<template>
  <div class="tti-main-view">
    <!-- ヘッダー -->
    <section>
      <nav class="tti-main-header-shadow">
        <div class="pull-left">
          <el-button class="backButton" @click="onBackButtonClick" v-tooltip="getTooltip(tooltipType.back)">
            <tti-svg-icon icon-name="wo_034"></tti-svg-icon>
          </el-button>
          <div class="tti-viewTitle">
            <tti-svg-icon icon-name="wo_008"></tti-svg-icon>
            <span>{{ $t('master.gpsTrackerExchange.viewTitle') }}</span>
          </div>
        </div>
      </nav>
    </section>
    <!-- テーブル -->
    <section class="tti-padding-content">
      <el-form
        label-width="120px"
        label-position="left"
        class="tti-form"
        ref="form"
        size="mini"
        :disabled="pageLock"
        :model="form"
        :rules="rules"
      >
        <div class="tti-lr-container">
          <!-- 端末解除情報 -->
          <div class="left">
            <div class="tti-content-title">
              <span class="contentTitle">{{ $t('master.gpsTrackerExchange.oldTerminal') }}</span>
            </div>
            <div class="content">
              <!-- SigfoxID -->
              <el-form-item
                :label="$t('master.gpsTrackerExchange.sigfoxID')"
                prop="oldSigfoxId"
                :rules="rules.oldSigfoxId"
              >
                <el-input
                  v-model.trim="form.oldSigfoxId"
                  class="input"
                  size="small"
                  @input="onOldSigFoxIdInput"
                ></el-input>
                <el-button class="tti-btn-default search-btn tti-btn-002" @click="onSearchBtnClick">
                  <tti-svg-icon icon-name="su_016"></tti-svg-icon>{{ $t('buttons.search') }}
                </el-button>
              </el-form-item>
              <div class="detail" v-if="isDetailShow">
                <div class="contentTitle">{{ $t('master.gpsTrackerExchange.detail') }}</div>
                <div class="detail-content" v-if="!isNoneMsgShow">
                  <!-- sigfoxid -->
                  <el-form-item
                    :label="$t(`master.gpsTrackerExchange.${item}`)"
                    v-for="item in detailTitle"
                    :key="item"
                  >
                    <span>{{ detailInfo[item] }}</span>
                  </el-form-item>
                </div>
                <div class="none" v-else>{{ $t('locationInfo.noData') }}</div>
              </div>
            </div>
          </div>

          <!-- 端末追加情報 -->
          <div class="right">
            <div class="tti-content-title">
              <span class="contentTitle">{{ $t('master.gpsTrackerExchange.newTerminal') }}</span>
            </div>
            <div class="content">
              <!-- SigfoxID -->
              <el-form-item
                :label="$t('master.gpsTrackerExchange.sigfoxID')"
                prop="newSigfoxId"
                :rules="rules.sigfoxId"
              >
                <el-input v-model.trim="form.newSigfoxId" class="input" size="small"></el-input>
              </el-form-item>
              <!-- シリアル番号 -->
              <el-form-item :label="$t('master.gpsTrackerExchange.serialNO')" prop="serialNo">
                <el-input v-model.trim="form.serialNo" class="input" size="small"></el-input>
              </el-form-item>
              <!-- 利用開始日 -->
              <el-form-item :label="$t('master.gpsTrackerExchange.startDate')" prop="startUseDate">
                <el-date-picker
                  v-model="form.startUseDate"
                  type="date"
                  class="input"
                  :picker-options="pickerOptions"
                  format="yyyy/MM/dd"
                  value-format="yyyy/MM/dd"
                  size="small"
                  :editable="false"
                  :sortable="true"
                  :clearable="false"
                ></el-date-picker>
              </el-form-item>
            </div>
          </div>
        </div>
      </el-form>
    </section>
    <!-- フッター -->
    <section class="tti-master-bottom-area">
      <div class="tti-bottom-left-area">
        <!-- 取消 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onCancelButtonClick"
          :disabled="pageLock"
          v-tooltip="getTooltip(tooltipType.cancel)"
          ><tti-svg-icon icon-name="su_005"></tti-svg-icon>{{ $t('buttons.cancel') }}</el-button
        >
      </div>
      <div class="tti-bottom-right-area">
        <!-- 保存 -->
        <el-button
          class="tti-btn-default tti-btn-001"
          @click="onSaveButtonClick"
          :disabled="pageLock || isSaveBtnDisable"
          v-tooltip="getTooltip(tooltipType.save)"
        >
          <tti-svg-icon icon-name="su_002"></tti-svg-icon>{{ $t('buttons.save') }}
        </el-button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { Component, Ref } from 'vue-property-decorator'
import { Form } from 'element-ui'
import { format } from 'date-fns'
import { cloneDeep } from 'lodash'

import { i18n } from '@/locales'
import HelperService from '@/service/helperService'
import { getTrackerDetail, updateTrackerExchangeInfo } from '@/store/apis'
import { General, TrackerDetail, TrackerExchangeForm } from '@/models'
import { RULES, VALID_PATTERN, validateService } from '@/service/validateService'
import { msgBoxService } from '@/commons/services/msgBoxService'
import MST_BASE from '@/views/master/MST_BASE.vue'
import { pickerOptions } from '@/utils/dateUtils'

/**
 * バッテリ交換画面
 *
 * @class
 */
@Component({
  name: 'MST_GPS_TRK_EXCHANGE'
})
export default class MST_GPS_TRK_EXCHANGE extends MST_BASE {
  /**
   * フォームのref
   */
  @Ref('form') readonly formRef!: Form

  /**
   * 画面ID
   */
  pageId = 'MST_GPS_TRK_EXCHANGE'

  /**
   * 詳細情報
   */
  detailTitle: (keyof TrackerDetail)[] = [
    // SigfoxId
    'sigfoxId',
    // シリアル番号
    'serialNo',
    // 節電モード
    'powerSavingMode',
    // 利用開始日
    'startUseDate',
    // 利用状況
    'usageStatus',
    // 管理対象コード
    'managementTargetCode',
    // 管理対象名
    'managementTargetName',
    // 営業所コード
    'businessOfficeCode',
    // 営業所名
    'businessOfficeName'
  ]

  /**
   * GPSトラッカー詳細
   */
  detailInfo: TrackerDetail = {} as TrackerDetail

  /**
   * タイムセレクタをクリックしたときの処理
   */
  pickerOptions = pickerOptions

  isSaveBtnDisable = true

  form = {
    startUseDate: format(new Date(), 'yyyy/MM/dd')
  } as TrackerExchangeForm

  isDetailShow = false

  isNoneMsgShow = false

  /**
   * 利用状況
   */
  usageStatusList: General[] = []

  /**
   * 節電モードリスト
   */
  managementPowerTypeList: General[] = []

  /**
   * インスタンスがマウントされたちょうど後にフック処理
   *
   * @returns {void}
   */
  mounted(): void {
    this.$log.debug('MST_GPS_TRK_EXCHANGE > mounted')
    this.checkPageLock()
    this.oldData = cloneDeep(this.form)
  }

  /**
   * 検索
   *
   *  @returns {void}
   */
  onSearchBtnClick(): void {
    this.formRef.validateField('oldSigfoxId', error => {
      if (error) {
        return
      }
      this.fetchDetail()
    })
  }

  /**
   * GPSトラッカー詳細取得
   */
  async fetchDetail(): Promise<void> {
    try {
      const res = await getTrackerDetail({ sigfoxId: this.form.oldSigfoxId })
      this.detailInfo = res
      this.isDetailShow = true
      this.isNoneMsgShow = false
      this.isSaveBtnDisable = false
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData: e=`, e)
      this.isDetailShow = true
      this.isNoneMsgShow = true
      this.isSaveBtnDisable = true
      throw e
    }
  }
  /**
   * 「取消」ボタンをクリックしたフラグ
   */
  cancelButtonClickFlag = false
  /**
   * 取消ボタンをクリックした時の処理。
   *
   * @returns {void}
   */
  onCancelButtonClick(): void {
    this.$log.debug(`MST_BASE > ${this.pageId} > onCancelButtonClick`)
    this.cancelButtonClickFlag = true
    if (!this.checkDataChanged()) {
      return
    }

    const confirmCallback = () => {
      this.resetForm()
    }

    msgBoxService.confirm(i18n.t('messages.masterCommon.cancelConfirm').toString(), confirmCallback)
  }

  /**
   * フォームをリセット
   */
  resetForm(): void {
    this.isDetailShow = false
    this.isSaveBtnDisable = true
    this.form = cloneDeep(this.oldData)
    this.formRef.clearValidate()
  }

  /**
   * 保存ボタンをクリックした時の処理
   *
   * @returns {Promise<boolean>} 保存結果
   */
  async saveTableData(): Promise<boolean> {
    try {
      if (!(await this.saveTableDataBefore())) {
        this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData > validation error`)
        return false
      }

      const confirmCallback = async () => {
        await this.saveTableDataAPI()
        await this.saveTableDataAfter()
      }

      msgBoxService.confirm(i18n.t('messages.masterCommon.saveConfirmWhenChanged').toString(), confirmCallback)

      return true
    } catch (e) {
      this.$log.debug(`MST_BASE > ${this.pageId} > saveTableData: e=`, e)
      throw e
    }
  }

  /**
   * 保存前に、チェックを行う
   *
   * @returns {Promise<boolean>} チェック結果
   */
  protected async saveTableDataBefore(): Promise<boolean> {
    try {
      const valid = await this.formRef.validate()
      if (!valid) {
        return false
      }

      return valid
    } catch {
      this.formRef.validate((valid, fields) => {
        if (!valid) {
          this.focusOnValidField(fields)
        }
      })
      return false
    }
  }

  /**
   * 保存API
   *
   * @returns {void}
   */
  async saveTableDataAPI(): Promise<void> {
    await updateTrackerExchangeInfo(this.form)
  }

  /**
   * 保存後に実行したい操作。
   *
   * @returns {void}
   */
  async saveTableDataAfter(): Promise<void> {
    const confirmCallback = () => {
      this.resetForm()
    }
    msgBoxService.info(i18n.t('messages.masterCommon.dataSaved').toString(), confirmCallback)
  }

  /**
   * データが変更されましたかをチェックする。
   *
   * @returns {boolean} isChanged
   */
  checkDataChanged(): boolean {
    if (this.cancelButtonClickFlag) {
      this.cancelButtonClickFlag = false
      return HelperService.isObjectDataChanged(this.oldData, this.form)
    } else {
      return false
    }
  }

  /**
   * sigfoxIdの値が変化した場合
   */
  onOldSigFoxIdInput(): void {
    this.isDetailShow = false
    this.isNoneMsgShow = false
    this.isSaveBtnDisable = true
  }

  /**
   * 検証ルール
   */
  rules = {
    // SigfoxID
    sigfoxId: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.sigfoxId')]).toString(),
        trigger: 'blur'
      },
      // 8文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.RANGE,
            range: [8, 8],
            message: i18n
              .t('vaildate.common.fixedAlphanumericWithLength', [i18n.t('master.gpsTrackerMaster.sigfoxId'), 8])
              .toString()
          }
        ]),
        trigger: 'blur'
      },
      // 複雑さチェック
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MUTIPLE,
            callback: (): string | undefined => {
              if (this.form.newSigfoxId === this.form.oldSigfoxId) {
                const message = i18n.t('vaildate.common.sigfoxIdRepeat').toString()
                return message
              }
              return
            }
          }
        ]),
        trigger: 'blur'
      }
    ],
    // SigfoxID
    oldSigfoxId: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.sigfoxId')]).toString(),
        trigger: 'blur'
      },
      // 8文字
      {
        validator: validateService.getValidator([
          {
            rule: RULES.RANGE,
            range: [8, 8],
            message: i18n
              .t('vaildate.common.fixedAlphanumericWithLength', [i18n.t('master.gpsTrackerMaster.sigfoxId'), 8])
              .toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // シリアル番号
    serialNo: [
      // 20文字以内
      {
        validator: validateService.getValidator([
          {
            rule: RULES.MAXLENGTH,
            number: 20,
            message: i18n.t('vaildate.common.maxlength', [i18n.t('master.gpsTrackerMaster.serialNo'), 20]).toString()
          }
        ]),
        trigger: 'blur'
      }
    ],
    // 利用開始日
    startUseDate: [
      // 必須
      {
        required: true,
        message: i18n.t('vaildate.common.required', [i18n.t('master.gpsTrackerMaster.startUseDate')]).toString(),
        trigger: 'blur'
      },
      // 日付
      {
        pattern: VALID_PATTERN[RULES.DATE],
        message: i18n.t('vaildate.common.date', [i18n.t('master.gpsTrackerMaster.startUseDate')]).toString(),
        trigger: 'blur'
      }
    ]
  }
}
</script>

<style lang="scss" scoped>
::v-deep .tti-bottom-right-area .tti-btn-default {
  margin-right: 0 !important;
}

::v-deep .input {
  width: 360px;
}

.content {
  ::v-deep .el-form-item {
    .el-form-item__label {
      line-height: 33px;
    }

    .el-form-item__content {
      display: flex;
      align-items: center;
    }
  }

  .detail {
    .contentTitle {
      font-size: $FS02;
      margin: 50px 0 20px 0;
      border-bottom: 1px solid #ccc;
    }

    .none {
      display: flex;
      justify-content: center;
      margin-top: 50px;
    }
  }
}
</style>
